import { useMemo } from 'react';
import { ApolloQueryResult, QueryResult } from '@apollo/client';

import {
  AdCampaignMetadataTypeFragment,
  AdCampaignPureType,
  AdPromoPureType,
  Exact,
  MediaAdCampaignQuery,
  useMediaAdCampaignLazyQuery,
} from '@shared/api/graphql/generated';
import { BaseQueryResultType, FetchPolicyKeys } from '@shared/api/graphql/types';

export type AdCampaignType = AdCampaignPureType;

export type AdPromoType = AdPromoPureType;

export type AdCampaignMetadataType = AdCampaignMetadataTypeFragment;

export type UseAdCampaignResultType = BaseQueryResultType & {
  refetch: (
    variables?:
      | Partial<
          Exact<{
            adCampaignId: string;
          }>
        >
      | undefined
  ) => Promise<ApolloQueryResult<MediaAdCampaignQuery>>;
  data: {
    adCampaign?: AdCampaignPureType;
  };
};

export type UseAdCampaignType = [
  request: (adCampaignId: string) => Promise<
    QueryResult<
      MediaAdCampaignQuery,
      Exact<{
        adCampaignId: string;
      }>
    >
  >,
  result: UseAdCampaignResultType,
];

const getFormattedTime = (time: string) => {
  if (!time) return undefined;

  const [mm, hh] = time.split(':');
  return `${mm}:${hh}`;
};

const useAdCampaign = (): UseAdCampaignType => {
  const [query, queryResult] = useMediaAdCampaignLazyQuery({
    fetchPolicy: FetchPolicyKeys.CACHE_AND_NETWORK,
  });

  const request = async (adCampaignId: string) =>
    query({
      variables: {
        adCampaignId,
      },
    });

  const adCampaign = useMemo(() => {
    if (queryResult.data?.adCampaignPure) {
      const adPromos = [...(queryResult.data?.adCampaignPure?.adPromos || [])]
        .reverse()
        .sort((a, b) => (b.time ? a.time?.localeCompare(b.time) : 1))
        .map((item) => ({
          ...item,
          time: item.time ? getFormattedTime(item.time) : item.time,
        }));

      return {
        ...(queryResult.data?.adCampaignPure as AdCampaignType),
        adPromos,
      };
    }

    return undefined;
  }, [queryResult.data?.adCampaignPure]);

  const result: UseAdCampaignResultType = {
    error: queryResult.error,
    loading: queryResult.loading,
    refetch: queryResult.refetch,
    data: {
      // TODO GERMAN fixe types
      // @ts-ignore-next-line
      adCampaign,
    },
  };

  return [request, result];
};

export default useAdCampaign;
